import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import Court from './components/Court'
import CourtMarkings from './components/CourtMarkings'
import { courtDimensions, drawingViewport } from './lib/courtGeometry'
import Trash from './components/Trash'
import PlayEditor from './components/PlayEditor'
import GameManager from './components/GameManager'
import GamePanel from './components/GamePanel'
import BoardControls from './components/BoardControls'
import { BackupPanel, ExportPanel } from './components/Exports'
import PlayAssignments from './components/PlayAssignments'
import { createPlay } from './lib/play'
import { icons } from './components/icons'
import { themeColors } from './lib/theme'
import { applyAlignment, boardsEqual, captureAlignment, cloneBoard, createBoard, defaultAlignmentPresets, displayPoint, layoutPlayers, mergeAlignmentPresets, swapPlayerLocations, type AlignmentPreset, type BoardState, type PlayerLayoutAction, type Tool } from './lib/board'
import { db, deleteAlignmentPreset, deleteDrawing, deletePlayDraft, listAlignmentPresets, listDrawings, loadDraft, loadModeDraft, openDraft, saveAlignmentPreset, switchDraftMode, saveDraft, saveDrawing, type SavedDrawing } from './lib/storage'
import { projectGame } from './lib/game'
import { useWhiteboardAnimation } from './lib/useWhiteboardAnimation'
import { AccountControl, AccountProvider } from './components/Account'

type View = 'home' | 'board' | 'library' | 'game' | 'editor'
type SaveStatus = 'loading' | 'saved' | 'saving' | 'error'

const tools: { id: Tool; label: string; icon: ReactNode }[] = [
  { id: 'select', label: 'Select', icon: icons.select },
  { id: 'animate', label: 'Animate', icon: icons.animate },
  { id: 'marker', label: 'Marker', icon: icons.marker },
  { id: 'line', label: 'Arrow', icon: icons.line },
  { id: 'eraser', label: 'Eraser', icon: icons.eraser },
]

function CoachApp({ view, setView }: { view: View; setView: (view: View) => void }) {
  const [board, setBoard] = useState<BoardState>(() => createBoard())
  const [tool, setTool] = useState<Tool>('select')
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null)
  const [undoStack, setUndoStack] = useState<BoardState[]>([])
  const [redoStack, setRedoStack] = useState<BoardState[]>([])
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('loading')
  const [huddle, setHuddle] = useState(false)
  const [showSave, setShowSave] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [drawings, setDrawings] = useState<SavedDrawing[]>([])
  const [toast, setToast] = useState<string | null>(null)
  const gestureStart = useRef<BoardState | null>(null)
  const currentBoard = useRef(board)
  const [ready, setReady] = useState(false)
  const saveRevision = useRef(0)
  const saveTimer = useRef<number | undefined>(undefined)
  const [modeBusy, setModeBusy] = useState(false)
  const [hasPlayDraft, setHasPlayDraft] = useState(false)
  const cancelAnimation = useRef<(() => void) | undefined>(undefined)
  const [showGamePanel, setShowGamePanel] = useState(false)
  const [showControls, setShowControls] = useState(false)
  const [alignmentPresets, setAlignmentPresets] = useState<AlignmentPreset[]>(() => structuredClone(defaultAlignmentPresets))
  const [editingPresets, setEditingPresets] = useState(false)
  const [editingPresetId, setEditingPresetId] = useState('')
  const [presetName, setPresetName] = useState('')
  const [presetBusy, setPresetBusy] = useState(false)
  const [presetSelection, setPresetSelection] = useState<string[]>([])
  const controlsLauncher = useRef<HTMLButtonElement>(null)
  const dismissControls = useCallback(() => setShowControls(false), [])
  const closeControls = useCallback(() => { setShowControls(false); controlsLauncher.current?.focus({ preventScroll: true }) }, [])
  const gameLauncher = useRef<HTMLButtonElement>(null)
  const returnToGamePanel = useRef(false)
  const closeGamePanel = useCallback(() => { setShowGamePanel(false); gameLauncher.current?.focus() }, [])
  const openGameDetails = useCallback(() => { returnToGamePanel.current = true; setShowGamePanel(false); cancelAnimation.current?.(); setView('game') }, [setView])

  useEffect(() => { if (view !== 'board' || huddle || showSave) { setShowGamePanel(false); setShowControls(false) } }, [view,huddle,showSave])

  useEffect(() => { const open = () => { cancelAnimation.current?.(); setView('game') }; window.addEventListener('open-game', open); return () => window.removeEventListener('open-game', open) }, [])

  useEffect(() => {
    Promise.all([loadDraft(), loadModeDraft('play')]).then(([draft, playDraft]) => {
      if (draft) setBoard(draft)
      setHasPlayDraft(!!(draft?.play || playDraft?.play))
      setSaveStatus('saved')
      setReady(true)
    }).catch(() => { setSaveStatus('error'); alert('Draft recovery failed. Reload to retry before editing; your stored draft has not been replaced.') })
  }, [])

  const refreshAlignmentPresets = useCallback(async () => setAlignmentPresets(mergeAlignmentPresets(await listAlignmentPresets())), [])
  useEffect(() => { refreshAlignmentPresets().catch(() => setToast('Custom presets could not be loaded.')) }, [refreshAlignmentPresets])

  useEffect(() => {
    if (!ready || modeBusy) return
    if (board.play) setHasPlayDraft(true)
    const revision = ++saveRevision.current
    setSaveStatus('saving')
    saveTimer.current = window.setTimeout(() => {
      saveDraft(board).then(() => { if (revision === saveRevision.current) setSaveStatus('saved') }).catch(() => { if (revision === saveRevision.current) setSaveStatus('error') })
    }, 450)
    return () => window.clearTimeout(saveTimer.current)
  }, [board, ready, modeBusy])

  currentBoard.current = board

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 2400)
    return () => window.clearTimeout(timer)
  }, [toast])

  const refreshDrawings = useCallback(async () => setDrawings(await listDrawings()), [])

  const openLibrary = async () => {
    try { await refreshDrawings(); setView('library') }
    catch { alert('Could not open saved boards. Please try again.'); setSaveStatus('error') }
  }

  const beginGesture = () => {
    gestureStart.current = cloneBoard(board)
  }

  const endGesture = () => {
    const before = gestureStart.current
    gestureStart.current = null
    if (before && !boardsEqual(before, currentBoard.current)) {
      setUndoStack((stack) => [...stack.slice(-49), before])
      setRedoStack([])
    }
  }

  const updateBoard = (updater: (current: BoardState) => BoardState) => {
    const next = updater(currentBoard.current)
    currentBoard.current = next
    setBoard(next)
  }

  const commit = (updater: (current: BoardState) => BoardState) => {
    const current = currentBoard.current
    const next = updater(current)
    if (!boardsEqual(current, next)) {
      setUndoStack((stack) => [...stack.slice(-49), cloneBoard(current)])
      setRedoStack([])
      currentBoard.current = next
      setBoard(next)
    }
  }

  const animation = useWhiteboardAnimation(board,ready && view === 'board' && tool === 'animate' && !board.play && !modeBusy,next => commit(() => next))
  cancelAnimation.current = animation.animator.cancel

  const undo = () => {
    const previous = undoStack.at(-1)
    if (!previous) return
    setRedoStack((redo) => [...redo, cloneBoard(board)])
    setBoard(previous)
    setUndoStack(undoStack.slice(0, -1))
  }

  const redo = () => {
    const next = redoStack.at(-1)
    if (!next) return
    setUndoStack((items) => [...items, cloneBoard(board)])
    setBoard(next)
    setRedoStack(redoStack.slice(0, -1))
  }

  const saveNamedDrawing = async () => {
    const title = saveName.trim() || 'Untitled board'
    try {
      const saved = await saveDrawing(board, title)
      setBoard(saved.board)
      setShowSave(false)
      setToast('Board saved to Playbook')
      setSaveStatus('saved')
    } catch {
      setSaveStatus('error')
      setToast('Save failed. Your recovery draft is still open.')
    }
  }

  const newBoard = () => {
    if ((board.play || board.strokes.length || board.title !== 'Untitled board') && !confirm('Start a new board? Save the current board first if you want to keep these edits.')) return
    void changeDraft(() => switchDraftMode(currentBoard.current, 'whiteboard', true), 'board')
  }

  const changeDraft = async (action: () => Promise<BoardState>, nextView: View, deleted = false) => {
    if (!ready || modeBusy) return
    window.clearTimeout(saveTimer.current)
    ++saveRevision.current
    setModeBusy(true)
    try {
      const next = await action()
      currentBoard.current = next
      setBoard(next)
      setUndoStack([]); setRedoStack([]); setSelectedPlayerId(null)
      gestureStart.current = null
      setHuddle(false); setShowSave(false); setTool('select'); setEditingPresets(false); setPresetSelection([])
      if (deleted) setHasPlayDraft(false)
      setView(nextView)
      setSaveStatus('saved')
    } catch {
      setSaveStatus('error')
      alert('Could not update drafts. Your current work is still open. Please try again.')
    } finally { setModeBusy(false) }
  }
  const switchToWhiteboard = () => void changeDraft(() => switchDraftMode(currentBoard.current, 'whiteboard'), 'board')
  const removePlayDraft = () => {
    if (confirm('Delete this play draft and its unsaved edits? This cannot be undone. Named copies in Playbook will be kept.')) {
      void changeDraft(deletePlayDraft, 'board', true)
    }
  }
  const editPlay = () => {
    if (board.play) { setView('editor'); return }
    if (hasPlayDraft && !confirm('Replace the existing play draft with a new play? Resume it first to keep editing it, or save it to Playbook.')) return
    const current = currentBoard.current
    void changeDraft(() => openDraft(current, { ...current, id: crypto.randomUUID(), play: createPlay(current), strokes: [] }), 'editor')
  }

  const chooseTool = (nextTool: Tool) => {
    if (settingUp && nextTool !== 'select') {
      updateBoard(current => ({ ...current, whiteboardPhase: 'drawing', updatedAt: Date.now() }))
      setSelectedPlayerId(null)
      setEditingPresets(false)
      setPresetSelection([])
    }
    setTool(nextTool)
  }

  const tradePlayerLocations = (id: string) => {
    const player = currentBoard.current.players.find(item => item.id === id)
    if (!player) return
    if (!selectedPlayerId) {
      setSelectedPlayerId(id)
      return
    }
    if (selectedPlayerId === id) {
      setSelectedPlayerId(null)
      return
    }
    const selected = currentBoard.current.players.find(item => item.id === selectedPlayerId)
    if (selected?.side !== player.side) {
      setSelectedPlayerId(id)
      return
    }
    commit(current => swapPlayerLocations(current, selectedPlayerId, id))
    setSelectedPlayerId(null)
  }

  const settingUp = !board.play && (board.whiteboardPhase || (board.strokes.length ? 'drawing' : 'setup')) === 'setup'

  const enterSetup = () => {
    animation.animator.cancel()
    updateBoard(current => ({ ...current, whiteboardPhase: 'setup', updatedAt: Date.now() }))
    setTool('select')
    setSelectedPlayerId(null)
    setShowControls(false)
    setShowGamePanel(false)
    setEditingPresets(false)
    setPresetSelection([])
  }

  const selectPreset = (preset: AlignmentPreset) => {
    commit(current => applyAlignment(current, preset))
    setEditingPresetId(preset.id)
    setPresetName(preset.label)
    setSelectedPlayerId(null)
    setPresetSelection([])
  }

  const togglePresetPlayer = (id: string) => {
    const player = currentBoard.current.players.find(item => item.id === id)
    if (player?.side !== 'offense') return
    setPresetSelection(selected => selected.includes(id) ? selected.filter(item => item !== id) : [...selected, id])
  }

  const arrangePresetPlayers = (action: PlayerLayoutAction) => {
    commit(current => layoutPlayers(current, presetSelection, action))
  }

  const storePreset = async (asNew: boolean) => {
    const label = presetName.trim()
    if (!label) { setToast('Name the preset before saving.'); return }
    if (!asNew && !editingPresetId) { setToast('Choose a preset to update, or save this as new.'); return }
    const id = asNew ? `custom-${crypto.randomUUID()}` : editingPresetId
    setPresetBusy(true)
    try {
      await saveAlignmentPreset({ id, label, positions: captureAlignment(currentBoard.current) })
      await refreshAlignmentPresets()
      setEditingPresetId(id)
      setToast(asNew ? `${label} added to presets` : `${label} preset updated`)
    } catch { setToast('Preset could not be saved. Please try again.') }
    finally { setPresetBusy(false) }
  }

  const removeOrResetPreset = async () => {
    const preset = alignmentPresets.find(item => item.id === editingPresetId)
    if (!preset) return
    const action = preset.builtIn ? 'reset to its original positions' : 'delete'
    if (!confirm(`${preset.label}: ${action}?`)) return
    setPresetBusy(true)
    try {
      await deleteAlignmentPreset(preset.id)
      await refreshAlignmentPresets()
      if (preset.builtIn) {
        const original = defaultAlignmentPresets.find(item => item.id === preset.id)!
        commit(current => applyAlignment(current, original))
        setPresetName(original.label)
        setToast(`${original.label} reset`)
      } else {
        setEditingPresetId('')
        setPresetName('')
        setToast(`${preset.label} deleted`)
      }
    } catch { setToast('Preset could not be changed. Please try again.') }
    finally { setPresetBusy(false) }
  }

  if (view === 'home') {
    return <Home onWhiteboard={() => setView('board')} onGame={() => setView('game')} onLibrary={openLibrary} />
  }

  if (view === 'library') {
    return <div inert={modeBusy}><Library drawings={drawings} onBack={() => setView('home')} onNew={newBoard} onOpen={(drawing) => { if (drawing.board.play && hasPlayDraft && drawing.id !== board.id && !confirm('Open this saved play and replace the existing play draft? Save your draft to Playbook first to keep it.')) return; void changeDraft(() => openDraft(currentBoard.current, cloneBoard(drawing.board)), 'board') }} onDelete={async (id) => { try { await deleteDrawing(id); await refreshDrawings() } catch { alert('Delete failed. Please try again.') } }} /><Trash onRestore={refreshDrawings} /><BackupPanel /></div>
  }

  if (view === 'game') {
    return <GameManager onBack={() => { setView('board'); if (returnToGamePanel.current) setShowGamePanel(true); returnToGamePanel.current = false }} />
  }

  if (view === 'editor') return <PlayEditor board={board} busy={modeBusy} onWhiteboard={switchToWhiteboard} onDeleteDraft={removePlayDraft} onBack={() => setView('board')} onDraft={(play, title, id) => updateBoard(current => ({ ...current, ...(id !== current.id ? { assignments: [], familyId: undefined, favorite: false, strokes: [] } : {}), id, play, title, players: play.initialPlayers, ballHandlerId: play.initialBall }))} onSave={value => { setBoard(value); setView('board'); setSaveName(value.title); setShowSave(true) }} />

  return (
    <main className={`app-shell ${huddle ? 'huddle' : ''}`} inert={modeBusy}>
      {!huddle && (
        <header className="topbar">
          <button className="brand-button" onClick={() => { animation.animator.cancel(); setView('home') }} aria-label="Back to home">{icons.home}<span>Coach Board</span></button>
          <div className="board-title-wrap">
            <span className="eyebrow">{board.play ? 'Animated play' : 'Quick Draw'}</span>
            <strong>{board.title}</strong>
          </div>
          <div className="save-state" role="status" data-status={saveStatus}><span />{saveStatus === 'loading' ? 'Restoring draft' : saveStatus === 'error' ? 'Save failed' : saveStatus === 'saving' ? 'Saving' : 'Saved locally'}</div>
          <div className="top-actions">
            <AccountControl compact />
            <button className="ghost-button" aria-label="Save" disabled={animation.busy} onClick={() => { setSaveName(board.title === 'Untitled board' ? '' : board.title); setShowSave(true) }}>{icons.save}<span>Save</span></button>
            <button className="primary-button" aria-label="Huddle" onClick={() => setHuddle(true)}>{icons.expand}<span>Huddle</span></button>
          </div>
        </header>
      )}

      <section className="workspace" inert={!ready}>
        {!huddle && (
          <aside className="tool-rail" aria-label="Drawing tools">
            <div className="board-tools">
            <button className={`setup-launcher ${settingUp ? 'active' : ''}`} disabled={!!board.play || animation.busy} onClick={enterSetup} aria-pressed={settingUp}>{icons.setup}<span>Setup</span></button>
            {tools.map((item) => <button key={item.id} disabled={!!board.play || (animation.busy && item.id !== 'animate')} className={tool === item.id ? 'active' : ''} onClick={() => chooseTool(item.id)} aria-pressed={tool === item.id}>{item.icon}<span>{item.label}</span></button>)}
            <div className="tool-divider" />
            <button onClick={undo} disabled={animation.busy || !undoStack.length}>{icons.undo}<span>Undo</span></button>
            <button onClick={redo} disabled={animation.busy || !redoStack.length}>{icons.redo}<span>Redo</span></button>
            </div>
            <button ref={controlsLauncher} className={`controls-launcher ${showControls ? 'active' : ''}`} aria-label="Board controls" aria-haspopup="dialog" aria-expanded={showControls} aria-controls="board-controls" disabled={animation.busy} onClick={() => { setShowGamePanel(false); setShowControls(value => !value) }}>{icons.controls}<span>Controls</span></button>
            <button ref={gameLauncher} className={`game-panel-launcher ${showGamePanel ? 'active' : ''}`} aria-label="Game management" aria-expanded={showGamePanel} aria-controls={showGamePanel ? 'game-popout' : undefined} onClick={() => { setShowControls(false); setShowGamePanel(value => !value) }}>{icons.players}<span>Game</span></button>
          </aside>
        )}

        <div className={`court-stage ${tool === 'animate' && !board.play ? 'animate-mode' : ''} ${settingUp && !board.play ? 'setup-mode' : ''} ${editingPresets ? 'preset-editing' : ''}`}>
          {settingUp && !board.play && !huddle && (editingPresets ? <div className="preset-editor-toolbar" aria-label="Preset editor" inert={presetBusy}>
            <div className="preset-editor-heading"><strong>Preset editor</strong><small>Drag or trade players on the court, then save their positions.</small></div>
            <label>Preset<select aria-label="Preset to edit" value={editingPresetId} onChange={event => { const preset = alignmentPresets.find(item => item.id === event.target.value); if (preset) selectPreset(preset); else { setEditingPresetId(''); setPresetName('') } }}><option value="">Current arrangement</option>{alignmentPresets.map(preset => <option key={preset.id} value={preset.id}>{preset.label}{preset.builtIn ? ' · built in' : ''}</option>)}</select></label>
            <label>Name<input aria-label="Preset name" maxLength={32} value={presetName} onChange={event => setPresetName(event.target.value)} placeholder="e.g. Horns" /></label>
            <div className="preset-editor-actions">
              <button disabled={!editingPresetId || !presetName.trim()} onClick={() => void storePreset(false)}>Update preset</button>
              <button disabled={!presetName.trim()} onClick={() => void storePreset(true)}>Save as new</button>
              {editingPresetId && <button className="preset-danger" onClick={() => void removeOrResetPreset()}>{alignmentPresets.find(item => item.id === editingPresetId)?.builtIn ? 'Reset default' : 'Delete'}</button>}
              <button onClick={() => { setEditingPresets(false); setPresetSelection([]) }}>Done</button>
            </div>
            <div className="preset-layout-tools" aria-label="Align and distribute players">
              <span>{presetSelection.length} selected</span>
              <button onClick={() => setPresetSelection(currentBoard.current.players.filter(player => player.side === 'offense').map(player => player.id))}>Select all</button>
              <button disabled={!presetSelection.length} onClick={() => setPresetSelection([])}>Clear</button>
              <button disabled={presetSelection.length < 2} onClick={() => arrangePresetPlayers('align-horizontal')}>Align horizontally</button>
              <button disabled={presetSelection.length < 2} onClick={() => arrangePresetPlayers('align-vertical')}>Align vertically</button>
              <button disabled={presetSelection.length < 3} onClick={() => arrangePresetPlayers('distribute-horizontal')}>Distribute horizontally</button>
              <button disabled={presetSelection.length < 3} onClick={() => arrangePresetPlayers('distribute-vertical')}>Distribute vertically</button>
            </div>
          </div> : <div className="setup-toolbar" aria-label="Player setup">
            <div className="formation-picker" aria-label="Preset alignments">
              <span>Alignment</span>
              {alignmentPresets.map(preset => <button key={preset.id} onClick={() => selectPreset(preset)}>{preset.label}</button>)}
              <button className="edit-presets" onClick={() => { setEditingPresets(true); setSelectedPlayerId(null); setPresetSelection([]) }}>Edit presets</button>
            </div>
            <p role="status">{selectedPlayerId ? `Now tap another ${board.players.find(player => player.id === selectedPlayerId)?.side} player to trade with ${board.players.find(player => player.id === selectedPlayerId)?.label}.` : 'Tap two teammates to trade locations, or drag anyone into place.'}</p>
            <button className="begin-drawing" onClick={() => { updateBoard(current => ({ ...current, whiteboardPhase: 'drawing', updatedAt: Date.now() })); setSelectedPlayerId(null); setEditingPresets(false); setPresetSelection([]); setTool('marker') }}>Begin drawing</button>
          </div>)}
          {tool === 'animate' && !board.play && <div className="animation-controls"><span role="status">{animation.status || 'Drag a player to move, or the ball to pass. Drop more actions to group them.'}</span>{animation.busy && <button onClick={animation.animator.cancel}>Cancel animation</button>}</div>}
          <div className={`court-frame ${board.fullCourt ? 'full-court' : ''}`}>
            <Court compact board={animation.frame || board} tool={tool} animation={tool === 'animate' ? animation : undefined} onGestureStart={beginGesture} onGestureEnd={endGesture} onChange={updateBoard} onSelectPlayer={(id) => setSelectedPlayerId(id || null)} onPlayerTap={editingPresets ? togglePresetPlayer : settingUp && !board.play ? tradePlayerLocations : undefined} selectedPlayerId={editingPresets ? null : selectedPlayerId} selectedPlayerIds={editingPresets ? presetSelection : undefined} readOnly={!!board.play} />
          </div>
          {huddle && <button className="exit-huddle" onClick={() => setHuddle(false)}>{icons.expand}<span>Exit Huddle</span></button>}
        </div>

      </section>

        <BoardControls open={showControls && !huddle && !showSave} onClose={closeControls} onDismiss={dismissControls} trigger={controlsLauncher}>
          <aside className="options-panel" inert={animation.busy}>
            <div><span className="eyebrow">Setup & plays</span><p>Set up the court, edit players, and manage plays.</p></div>
            <button className="option-card" onClick={() => commit((current) => ({ ...current, showDefense: !current.showDefense, updatedAt: Date.now() }))}>{icons.shield}<span><strong>{board.showDefense ? 'Hide defense' : 'Add defense'}</strong><small>{board.showDefense ? 'Remove red markers' : 'Five red markers'}</small></span><b>{board.showDefense ? 'On' : 'Off'}</b></button>
            <button className="option-card" onClick={() => commit((current) => ({ ...current, flipped: !current.flipped, updatedAt: Date.now() }))}>{icons.flip}<span><strong>Flip court</strong><small>Geometry stays fixed</small></span></button>
            <button className="option-card" onClick={() => commit(current => ({ ...current, fullCourt: !current.fullCourt }))}>{board.fullCourt ? 'Show half court' : 'Show full court'}</button>
            <button className="option-card" onClick={editPlay}>{board.play ? 'Edit animated play' : 'Create animated play'}</button>
            {board.play ? <><button className="option-card" onClick={switchToWhiteboard}>Switch to Whiteboard</button><button className="danger-link" onClick={removePlayDraft}>Delete draft play</button></> : hasPlayDraft && <button className="option-card" onClick={() => void changeDraft(() => switchDraftMode(currentBoard.current, 'play'), 'editor')}>Resume play draft</button>}
            {selectedPlayerId && !board.play && <><label>Player label<input maxLength={12} value={board.players.find(p => p.id === selectedPlayerId)?.label || ''} onChange={event => commit(current => ({ ...current, players: current.players.map(p => p.id === selectedPlayerId ? { ...p, label: event.target.value } : p) }))} /></label><button className="option-card" onClick={() => commit(current => ({ ...current, ballHandlerId: selectedPlayerId }))}>Give selected player the ball</button></>}
            <button className="option-card" onClick={() => commit(current => ({ ...cloneBoard(current), familyId: current.familyId || current.id, id: crypto.randomUUID(), title: `${current.title} variation` }))}>Duplicate / create variation</button>
            <button className="option-card" onClick={() => commit(current => ({ ...current, favorite: !current.favorite }))}>{board.favorite ? 'Remove favorite' : 'Add favorite'}</button>
            <PlayAssignments board={board} onChange={value => commit(() => value)} />
            <ExportPanel board={board} />
            <button className="text-action" disabled={!board.strokes.length} onClick={() => { if (confirm('Clear all annotations? Players will stay in place.')) commit((current) => ({ ...current, strokes: [], updatedAt: Date.now() })) }}>Clear annotations</button>
            <div className="tip"><span>Tip</span>{board.play ? 'Open Edit animated play to change players, paths, and step annotations.' : 'Drag players beyond either sideline or baseline to set up inbound plays. Use Marker for ink and Arrow for structured lines.'}</div>
          </aside>
        </BoardControls>

      {showGamePanel && !huddle && !showSave && <GamePanel onClose={closeGamePanel} onDetails={openGameDetails} />}
      {showSave && <SaveDialog name={saveName} onName={setSaveName} onCancel={() => setShowSave(false)} onSave={saveNamedDrawing} />}
      {toast && <div className="toast" role="status">{toast}</div>}
    </main>
  )
}

function Home({ onWhiteboard, onGame, onLibrary }: { onWhiteboard: () => void; onGame: () => void; onLibrary: () => void }) {
  return (
    <main className="home-screen">
      <header className="home-header"><div className="brand-mark">CB</div><div><span className="eyebrow">Basketball Coach</span><h1>What are we working on?</h1></div><div className="home-account-actions"><span className="offline-pill"><i />Offline ready</span><AccountControl /></div></header>
      <section className="home-grid">
        <button className="home-card featured" onClick={onWhiteboard}><span className="card-icon">{icons.marker}</span><span><small>Fastest path</small><h2>Whiteboard</h2><p>Sketch a set, move players, and walk into the huddle.</p></span><b>Open board →</b></button>
        <button className="home-card" onClick={onGame}><span className="card-icon warm">05</span><span><small>Live tools</small><h2>Start Game</h2><p>Set starters and track court time, fouls, and substitutions.</p></span><b>Set up game →</b></button>
        <button className="home-card" onClick={onLibrary}><span className="card-icon green">{icons.board}</span><span><small>Saved locally</small><h2>Playbook</h2><p>Return to saved boards and build your library.</p></span><b>View playbook →</b></button>
      </section>
      <footer className="home-footer">Designed for iPad landscape · Pencil and touch · Works offline</footer>
    </main>
  )
}

function CourtThumbnail({ board }: { board: BoardState }) {
  const { width, height } = courtDimensions(!!board.fullCourt)
  const view = drawingViewport(board)
  return <div className="mini-court"><svg viewBox={`${view.x} ${view.y} ${view.width} ${view.height}`} width="100%" height="100%" aria-hidden="true"><CourtMarkings fullCourt={board.fullCourt} flipped={board.flipped} apron={view} />{board.players.filter(p => p.side === 'offense' || board.showDefense).map(p => { const shown = displayPoint(p,board.flipped); return <circle key={p.id} cx={shown.x*width} cy={shown.y*height} r="18" fill={p.side === 'offense' ? themeColors.electricBlue : themeColors.danger} stroke="white" strokeWidth="3" /> })}</svg></div>
}

function Library({ drawings, onBack, onNew, onOpen, onDelete }: { drawings: SavedDrawing[]; onBack: () => void; onNew: () => void; onOpen: (drawing: SavedDrawing) => void; onDelete: (id: string) => void }) {
  const [query, setQuery] = useState(''), [team, setTeam] = useState(''), [favorites, setFavorites] = useState(false)
  const [teams, setTeams] = useState<import('./lib/game').Team[]>([])
  useEffect(() => { db.teams.toArray().then(setTeams).catch(() => {}) }, [])
  const visible = drawings.filter(d => (!favorites || d.board.favorite) && (!team || d.board.assignments?.some(a => a.teamId === team)) && `${d.title} ${d.board.assignments?.map(a => `${a.alias} ${a.category} ${a.notes}`).join(' ') || ''}`.toLowerCase().includes(query.toLowerCase()))
  const families = [...new Set(visible.map(d => d.board.familyId || d.id))]
  return <main className="page-shell"><header className="page-header"><button className="brand-button" onClick={onBack}>{icons.home}<span>Home</span></button><div><span className="eyebrow">Saved locally</span><h1>Playbook</h1></div><button className="primary-button" onClick={onNew}>{icons.plus}<span>New board</span></button></header><div className="library-filters"><input aria-label="Search playbook" placeholder="Search names, categories, notes" value={query} onChange={e => setQuery(e.target.value)} /><select aria-label="Filter by team" value={team} onChange={e => setTeam(e.target.value)}><option value="">All teams</option>{teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select><label><input type="checkbox" checked={favorites} onChange={e => setFavorites(e.target.checked)} />Favorites</label></div>
  {families.map(family => <section className="library-family" key={family}><h2>{drawings.find(d => d.id === family)?.title || 'Play variations'}</h2><div className="library-grid">{visible.filter(d => (d.board.familyId || d.id) === family).map(drawing => <article className="drawing-card" key={drawing.id}><CourtThumbnail board={drawing.board} /><div><h2>{team ? drawing.board.assignments?.find(a => a.teamId === team)?.alias || drawing.title : drawing.title}{drawing.board.favorite ? ' ★' : ''}</h2><p>{drawing.board.play ? `${drawing.board.play.steps.length} animated steps` : 'Drawing'} · Updated {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(drawing.savedAt)}</p>{team && <p>{drawing.board.assignments?.find(a => a.teamId === team)?.notes}</p>}</div><div className="drawing-actions"><button onClick={() => onOpen(drawing)}>Open</button><button className="danger-link" onClick={() => { if (confirm(`Move ${drawing.title} to Recently Deleted?`)) onDelete(drawing.id) }}>Delete</button></div></article>)}</div></section>)}
  {!visible.length && <div className="empty-state"><span className="card-icon green">{icons.board}</span><h2>{drawings.length ? 'No matching boards' : 'No saved boards yet'}</h2><p>Save a named board to add it here.</p><button className="primary-button" onClick={onNew}>Create a board</button></div>}</main>
}

function Placeholder({ title, detail, onBack }: { title: string; detail: string; onBack: () => void }) {
  return <main className="placeholder-screen"><div className="brand-mark">CB</div><span className="eyebrow">Planned milestone</span><h1>{title}</h1><p>{detail}</p><button className="primary-button" onClick={onBack}>Back home</button></main>
}

function SaveDialog({ name, onName, onCancel, onSave }: { name: string; onName: (value: string) => void; onCancel: () => void; onSave: () => void }) {
  return <div className="modal-backdrop" role="presentation" onPointerDown={(event) => { if (event.target === event.currentTarget) onCancel() }}><section className="modal" role="dialog" aria-modal="true" aria-labelledby="save-title"><span className="eyebrow">Playbook</span><h2 id="save-title">Name this board</h2><p>Your board is already recovering locally. A name makes it easy to find later.</p><label>Board name<input autoFocus value={name} onChange={(event) => onName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') onSave() }} placeholder="e.g. Horns elbow entry" /></label><div><button className="ghost-button" onClick={onCancel}>Cancel</button><button className="primary-button" onClick={onSave}>Save board</button></div></section></div>
}

function AppContent() {
  const [view, setView] = useState<View>('home')
  const [gameStatus, setGameStatus] = useState('Game Management')
  useEffect(() => {
    let alive = true
    const refresh = async () => {
      try {
        const games = await db.games.toArray()
        const active = games.find(g => projectGame(g, Date.now()).status !== 'ended')
        if (alive) setGameStatus(active ? `Game: ${projectGame(active, Date.now()).status === 'paused' ? 'PAUSED' : 'tracking'}` : 'Game Management')
      } catch { /* The game screen reports storage failures. */ }
    }
    void refresh(); const timer = setInterval(refresh, 2000)
    return () => { alive = false; clearInterval(timer) }
  }, [])
  return <><CoachApp view={view} setView={setView} />{view !== 'board' && view !== 'game' && <button className="global-game-button" onClick={() => window.dispatchEvent(new Event('open-game'))}>{gameStatus}</button>}{view !== 'home' && view !== 'board' && <div className="global-account-control"><AccountControl compact /></div>}</>
}

function App() {
  return <AccountProvider><AppContent /></AccountProvider>
}

export default App
